import { useState, useEffect, useCallback } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import { Sidebar } from './Sidebar'
import { ShieldLogo } from './ShieldLogo'
import { FeedbackWidget } from './FeedbackWidget'
import { OnboardingGuide, useShowGuide } from './OnboardingGuide'
import { useOffice } from '../contexts/OfficeContext'
import { useLang } from '../contexts/LangContext'
import { supabase } from '../lib/supabase'
import { SETTINGS_URLS } from '../lib/settingsParams'

function LayoutInner() {
  const { officeName, loading, officeStatus, accessUntil, paymentPastDue, officeId, onboardingCompleted, refreshPlan, userRole } = useOffice()
  // Billing actions are admin-only (reactivate-subscription answers 403 to an
  // agent and the Plan card in Settings is admin-only). An agent sees the same
  // status, with no billing buttons and a pointer to the office administrator.
  const isAdmin = userRole === 'admin'
  const { t } = useLang()
  const navigate = useNavigate()
  const [reactivating, setReactivating] = useState(false)
  const [reactivateError, setReactivateError] = useState<string | null>(null)

  // Compute days remaining for pending_cancellation countdown
  const daysUntilExpiry = accessUntil
    ? Math.max(0, Math.ceil((new Date(accessUntil).getTime() - Date.now()) / 86_400_000))
    : null

  const handleReactivate = useCallback(async () => {
    if (!officeId) return
    setReactivating(true)
    setReactivateError(null)

    const { data: { session } } = await supabase.auth.getSession()
    const token = session?.access_token
    // No session: say so instead of a click that silently does nothing.
    if (!token) { setReactivateError(t('acqAdmin.errors.session')); setReactivating(false); return }

    try {
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/reactivate-subscription`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            office_id:   officeId,
            success_url: `${window.location.origin}${SETTINGS_URLS.POST_REACTIVATION}`,
            cancel_url:  `${window.location.origin}${SETTINGS_URLS.PLAN_TAB}`,
          }),
        },
      )

      const json = await res.json() as { success?: boolean; checkout_url?: string; error?: string; scenario?: string }

      if (!res.ok || json.error) {
        // The function answers in English (and one case in Portuguese): show a
        // message in the panel's language and keep the raw text in the console.
        console.error('[reactivate] failed:', res.status, json.error)
        setReactivateError(
          res.status === 401 ? t('acqAdmin.errors.session')
          // 409: a live, unpaid subscription still exists; the fix is its payment, not a new one.
          : res.status === 409 ? t('billing.reactivatePendingSubscription')
          // 422: no Stripe customer or price for the plan; only support can fix it.
          : res.status === 422 ? t('billing.reactivateContactSupport')
          : t('billing.reactivateError'),
        )
        setReactivating(false)
        return
      }

      // Scenario B — redirect to Stripe Checkout for new subscription
      if (json.checkout_url) {
        window.location.href = json.checkout_url
        return
      }

      // Scenario A — already reactivated; refresh plan state
      await refreshPlan()
    } catch {
      setReactivateError(t('billing.reactivateError'))
    }
    setReactivating(false)
  }, [officeId, refreshPlan, t])
  // Product tour — shows once after first login
  const showGuide = useShowGuide(officeId, onboardingCompleted ?? false)
  const [guideVisible, setGuideVisible] = useState(false)
  useEffect(() => { if (showGuide) setGuideVisible(true) }, [showGuide])

  const [mobileOpen, setMobileOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('sidebar-collapsed') === 'true' } catch { return false }
  })

  useEffect(() => {
    try { localStorage.setItem('sidebar-collapsed', String(collapsed)) } catch {}
  }, [collapsed])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg-page)' }}>
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-gray-600" />
      </div>
    )
  }

  return (
    <div className="min-h-screen font-panel" style={{ backgroundColor: 'var(--bg-page)' }}>
      {/* Billing banners take the same offset as <main>: the fixed sidebar used to cover
          their start, hiding the lock icon and the first words of the message. */}
      <div className={`transition-all duration-200 ${collapsed ? 'md:ml-16' : 'md:ml-64'}`}>
        {/* Pending cancellation banner — cancel_at_period_end=true, access expires soon */}
        {/* Not shown once suspended: the red banner below takes over, and the two
            used to stack, each offering its own "Reativar" for a different flow. */}
        {accessUntil && officeStatus !== 'suspended' && (
          <div className="mt-14 md:mt-0 px-4 py-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between" style={{ backgroundColor: 'var(--bg-warning)', borderBottom: '1px solid var(--border-warning)' }}>
            <p className="text-sm font-medium" style={{ color: 'var(--text-warning)' }}>
              ⏳ {isAdmin
                ? t('billing.pendingCancellation', { days: String(daysUntilExpiry ?? 0) })
                : t('billing.agent.pendingCancellation', { days: String(daysUntilExpiry ?? 0) })}
            </p>
            {isAdmin && (
            <div className="flex items-center gap-2 flex-shrink-0">
              {reactivateError && (
                <span className="text-xs" style={{ color: 'var(--tone-red)' }}>{reactivateError}</span>
              )}
              <button
                onClick={handleReactivate}
                disabled={reactivating}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-opacity disabled:opacity-60 whitespace-nowrap"
                style={{ backgroundColor: 'var(--text-warning)', color: 'var(--on-accent)' }}
              >
                {reactivating ? t('billing.reactivating') : t('billing.reactivate')}
              </button>
            </div>
            )}
          </div>
        )}
        {/* Suspended account banner — subscription fully deleted */}
        {officeStatus === 'suspended' && (
          <div className="mt-14 md:mt-0 px-4 py-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between" style={{ backgroundColor: 'var(--tone-red-bg)', borderBottom: '1px solid color-mix(in srgb, var(--tone-red) 20%, transparent)' }}>
            <p className="text-sm font-medium" style={{ color: 'var(--tone-red)' }}>
              🔒 {isAdmin ? t('billing.suspended') : t('billing.agent.suspended')}
            </p>
            {isAdmin && (
            <div className="flex items-center gap-2 flex-shrink-0">
              {reactivateError && (
                <span className="text-xs" style={{ color: 'var(--tone-red)' }}>{reactivateError}</span>
              )}
              <button
                onClick={handleReactivate}
                disabled={reactivating}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-opacity disabled:opacity-60 whitespace-nowrap"
                style={{ backgroundColor: 'var(--tone-red)', color: 'var(--on-accent)' }}
              >
                {reactivating ? t('billing.reactivating') : t('billing.reactivate')}
              </button>
              <button
                onClick={() => navigate(SETTINGS_URLS.PLAN_TAB)}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg whitespace-nowrap"
                style={{ backgroundColor: 'color-mix(in srgb, var(--tone-red) 15%, transparent)', color: 'var(--tone-red)', border: '1px solid color-mix(in srgb, var(--tone-red) 25%, transparent)' }}
              >
                {t('billing.updatePayment')}
              </button>
            </div>
            )}
          </div>
        )}
        {/* Pending payment — quick-signup user abandoned Stripe checkout */}
        {officeStatus === 'pending_payment' && (
          <div className="mt-14 md:mt-0 px-4 py-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between" style={{ backgroundColor: 'var(--bg-warning)', borderBottom: '1px solid var(--border-warning)' }}>
            <p className="text-sm font-medium" style={{ color: 'var(--text-warning)' }}>
              💳 {isAdmin ? t('billing.pendingPayment') : t('billing.agent.pendingPayment')}
            </p>
            {isAdmin && (
            <button
              onClick={() => navigate(SETTINGS_URLS.COMPLETE_PAYMENT)}
              className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-opacity whitespace-nowrap"
              style={{ backgroundColor: 'var(--text-warning)', color: 'var(--on-accent)' }}
            >
              {t('billing.completePayment')}
            </button>
            )}
          </div>
        )}
        {/* Payment past due — Stripe retrying, amber warning */}
        {officeStatus !== 'suspended' && !accessUntil && officeStatus !== 'pending_payment' && paymentPastDue && (
          <div className="mt-14 md:mt-0 px-4 py-3 flex items-center justify-between" style={{ backgroundColor: 'var(--bg-warning)', borderBottom: '1px solid var(--border-warning)' }}>
            <p className="text-sm font-medium" style={{ color: 'var(--text-warning)' }}>
              ⚠️ {isAdmin ? t('billing.paymentPastDue') : t('billing.agent.paymentPastDue')}
            </p>
            {isAdmin && (
            <button
              onClick={() => navigate(SETTINGS_URLS.PLAN_TAB)}
              className="text-xs font-semibold px-3 py-1 rounded-lg"
              style={{ backgroundColor: 'var(--text-warning)', color: 'var(--on-accent)' }}
            >
              {t('billing.updatePayment')}
            </button>
            )}
          </div>
        )}
      </div>
      <Sidebar
        officeName={officeName}
        mobileOpen={mobileOpen}
        onClose={() => setMobileOpen(false)}
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed((c) => !c)}
      />

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 flex items-center gap-3 px-4 py-3 border-b" style={{ backgroundColor: 'var(--nav-bg)', borderColor: 'rgba(255,255,255,0.06)' }}>
        <button onClick={() => setMobileOpen(true)} aria-label={t('landing.menuOpen')} className="text-gray-400 hover:text-white transition-colors">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
          </svg>
        </button>
        {/* Same mark as the sidebar: the official shield, name and tagline */}
        <div className="flex items-center gap-2 min-w-0">
          <ShieldLogo size={30} />
          <div className="min-w-0">
            <div className="text-white font-extrabold text-sm leading-tight tracking-tight">Immigrant Shield</div>
            <div className="text-[9px] font-bold uppercase tracking-[0.2em] leading-tight" style={{ color: 'var(--nav-accent)' }}>for Offices</div>
          </div>
        </div>
      </div>

      <main className={`transition-all duration-200 px-4 md:px-8 pt-16 md:pt-8 pb-8 min-w-0 ${
        collapsed ? 'md:ml-16' : 'md:ml-64'
      }`}>
        <Outlet />
      </main>

      <FeedbackWidget />

      {/* Product tour — first-time user guide */}
      {guideVisible && (
        <OnboardingGuide
          officeId={officeId}
          onDismiss={() => setGuideVisible(false)}
        />
      )}
    </div>
  )
}

export function AppLayout() {
  return <LayoutInner />
}
